import React, { useState, useEffect } from "react";
import {
  Box, Button, Stack, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Chip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tooltip, Switch, FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyIcon from "@mui/icons-material/Key";
import { botsApi } from "../../../api/bots.js";
import { EmptyState } from "../../../components/EmptyState.js";

interface Invite {
  code_prefix: string; max_uses: number; current_uses: number;
  created_at: string; is_revoked: boolean; expires_at?: string;
}

export function InvitesTab({ botId, inviteRequired, onUpdate }: { botId: string; inviteRequired: boolean; onUpdate: () => void }) {
  const [invites, setInvites]   = useState<Invite[]>([]);
  const [open, setOpen]         = useState(false);
  const [newCode, setNewCode]   = useState<string | null>(null);
  const [copied, setCopied]     = useState(false);
  const [maxUses, setMaxUses]   = useState("-1");
  const [toggling, setToggling] = useState(false);

  const load = () => { botsApi.listInvites(botId).then(setInvites).catch(console.error); };
  useEffect(load, [botId]);

  const toggleGating = async () => {
    setToggling(true);
    try {
      await botsApi.update(botId, { invite_required: !inviteRequired });
      onUpdate();
    } finally {
      setToggling(false);
    }
  };

  const generate = async () => {
    const res = await botsApi.generateInvite(botId, { max_uses: parseInt(maxUses) || -1 });
    setNewCode(res.code);
    setOpen(false);
    load();
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const revoke = async (prefix: string) => {
    await botsApi.revokeInvite(botId, prefix);
    load();
  };

  return (
    <Box>
      {/* Toggle row */}
      <Stack direction="row" alignItems="center" mb={3}>
        <FormControlLabel
          control={
            <Switch
              checked={inviteRequired}
              onChange={toggleGating}
              disabled={toggling}
              color="primary"
            />
          }
          label={<Typography variant="body1" fontWeight={500}>Protect bot with code:</Typography>}
          labelPlacement="end"
          sx={{ ml: 0 }}
        />
      </Stack>

      {/* Invite codes section — greyed out when protection is off */}
      <Box
        sx={{
          opacity: inviteRequired ? 1 : 0.4,
          pointerEvents: inviteRequired ? "auto" : "none",
          transition: "opacity 0.2s",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5">Invite Codes</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Generate Code</Button>
        </Stack>

        {newCode && (
          <Alert
            severity="success" sx={{ mb: 2 }}
            action={
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copy(newCode)}>
                {copied ? "Copied!" : "Copy"}
              </Button>
            }
          >
            <strong>Save this code — it won't be shown again:</strong>&nbsp;
            <code style={{ fontFamily: "monospace", fontSize: 14 }}>{newCode}</code>
          </Alert>
        )}

        {invites.length === 0 ? (
          <EmptyState
            icon={<KeyIcon />}
            title="No invite codes"
            description="Generate a code to share with users who need access."
            action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Generate Code</Button>}
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Prefix</TableCell>
                <TableCell>Uses</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.map(inv => (
                <TableRow key={inv.code_prefix} hover>
                  <TableCell><code style={{ fontFamily: "monospace" }}>{inv.code_prefix}…</code></TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {inv.current_uses} / {inv.max_uses === -1 ? "∞" : inv.max_uses}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{new Date(inv.created_at).toLocaleDateString()}</Typography></TableCell>
                  <TableCell>
                    <Chip
                      label={inv.is_revoked ? "Revoked" : "Active"}
                      color={inv.is_revoked ? "error" : "success"} size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {!inv.is_revoked && (
                      <Tooltip title="Revoke">
                        <IconButton size="small" onClick={() => revoke(inv.code_prefix)} sx={{ color: "error.main" }}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate invite code</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Max uses" type="number" fullWidth
              value={maxUses} onChange={e => setMaxUses(e.target.value)}
              helperText="-1 = unlimited"
              InputProps={{ inputProps: { min: -1 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={generate}>Generate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
